import { z } from 'zod';
import { BibleLocationSchema } from '../bible/bible-location.js';
/**
 * A verse chunk for embedding in the vector database.
 */
export const VerseChunkSchema = z.object({
    id: z.string(),
    text: z.string(),
    metadata: z.object({
        type: z.literal('bible_verse'),
        reference: z.string(), // "John 3:16"
        location: BibleLocationSchema,
        bookName: z.string(),
        chapterNumber: z.number().int().positive(),
        verseNumber: z.number().int().positive(),
    }),
});
/**
 * Textual variant chunk comparing KJV to other manuscripts.
 */
export const TextualVariantChunkSchema = z.object({
    id: z.string(),
    text: z.string(),
    metadata: z.object({
        type: z.literal('textual_variant'),
        reference: z.string(),
        location: BibleLocationSchema,
        category: z.enum([
            'word_substitution',
            'addition',
            'omission',
            'major_addition',
            'full_verse_omission',
        ]),
        kjvText: z.string(),
        kjvStrongs: z.array(z.string()),
        nasbStrongs: z.array(z.string()),
        differences: z.object({
            strongsOnlyInKJV: z.array(z.string()),
            strongsOnlyInNASB: z.array(z.string()),
            significantWords: z.array(z.object({
                strong: z.string(),
                word: z.string(),
            })),
        }),
        manuscriptEvidence: z
            .object({
            textusReceptus: z.string(),
            sinaiticus: z.string().optional(),
            vaticanus: z.string().optional(),
        })
            .optional(),
        theologicalImpact: z.enum(['low', 'medium', 'high']),
        affectedConcepts: z.array(z.string()).optional(),
        categoryTags: z.array(z.string()),
    }),
});
/**
 * Commentary chunk from Halley's, McGee, etc.
 */
export const CommentaryChunkSchema = z.object({
    id: z.string(),
    text: z.string(),
    metadata: z.object({
        type: z.literal('commentary'),
        source: z.object({
            type: z.enum(['halley', 'mcgee', 'chuck-smith']),
            author: z.string(),
            title: z.string(),
            series: z.string().optional(),
        }),
        books: z.array(z.object({
            name: z.string(),
            testament: z.enum(['OT', 'NT']),
            bookNumber: z.number().int().nonnegative(),
            testamentNumber: z.number().int().nonnegative(),
        })),
        startLocation: BibleLocationSchema.optional(),
        endLocation: BibleLocationSchema.optional(),
        verseReferences: z.array(z.string()).optional(),
        topics: z.array(z.string()).optional(),
        chunkIndex: z.number().int().nonnegative(),
        filename: z.string(),
        sectionType: z
            .enum(['introduction', 'outline', 'commentary', 'recommended_books', 'other'])
            .optional(),
    }),
});
/**
 * Cross-reference chunk linking verses.
 */
export const CrossReferenceChunkSchema = z.object({
    id: z.string(),
    text: z.string(),
    metadata: z.object({
        type: z.literal('cross_reference'),
        location: BibleLocationSchema,
        references: z.array(z.object({
            reference: z.object({
                location: BibleLocationSchema,
            }),
            votes: z.number().int().nonnegative(),
        })),
    }),
});
/**
 * Jones textual criticism chunk from Floyd Nolen Jones' "Ripped Out of the Bible".
 */
export const JonesChunkSchema = z.object({
    id: z.string(),
    text: z.string(),
    metadata: z.object({
        type: z.literal('jones_textual_criticism'),
        section: z.number().int().min(1).max(5),
        sectionTitle: z.string(),
        category: z.enum(['passage_missing', 'deity_name_missing', 'hell_diminished', 'word_count']),
        reference: z.string().optional(),
        kjvText: z.string().optional(),
        nivText: z.string().optional(),
        omittedContent: z.string().optional(),
        book: z.string().optional(),
        chapter: z.number().int().positive().optional(),
        stephanusWordCount: z.number().int().positive().optional(),
        nestleAlandWordCount: z.number().int().positive().optional(),
        wordCountDifference: z.number().int().optional(),
    }),
});
//# sourceMappingURL=chunk-types.js.map